import React, { useState } from 'react';
import { AlertTriangle, Filter, ArrowUpRight, ArrowDownRight, X } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from 'recharts';
import { useAppState } from '../store/AppContext';
import { Card, CardBody } from './ui/Card';
import { AssetStructureModule } from './AssetStructureModule';
import {
  getAssetUnrealizedGain, getAssetUnrealizedGainPercent, getAssetCurrentValue,
  getAssetCostBasis, getTotalNAV, getTotalRealizedGain, getBucketProgress,
} from '../utils/calculations';
import { formatVND } from '../utils/format';
import { Money, SensitiveValue } from './ui/SensitiveValue';
import { resolveClassColor } from '../utils/calculations';

const tooltip = {
  contentStyle: { backgroundColor: 'var(--tooltip-bg)', border: '1px solid var(--tooltip-bd)', borderRadius: 10, fontSize: 12, color: 'var(--tx-1)' },
  labelStyle: { color: 'var(--tx-3)' }, itemStyle: { color: 'var(--tx-3)' },
  cursor: { fill: 'var(--chart-grid)' },
};

const TX_LABELS: Record<string, { label: string; color: string }> = {
  BUY: { label: 'Mua', color: '#38bdf8' },
  SELL: { label: 'Bán', color: '#f87171' },
  DIVIDEND: { label: 'Cổ tức', color: '#10b981' },
  INTEREST: { label: 'Lãi', color: '#10b981' },
  TRANSFER_IN: { label: 'Chuyển vào', color: '#a78bfa' },
  TRANSFER_OUT: { label: 'Chuyển ra', color: '#fbbf24' },
};

export function AnalyticsHub() {
  const { state } = useAppState();
  const { assets, transactions, buckets, assetClasses } = state;

  const [filterClass, setFilterClass] = useState('');
  const [filterBucket, setFilterBucket] = useState('');

  const filtered = assets.filter(a =>
    (!filterClass || a.assetClass === filterClass) &&
    (!filterBucket || a.bucketId === filterBucket)
  );

  const totalNAV = getTotalNAV(filtered);
  const unrealized = filtered.reduce((s, a) => s + getAssetUnrealizedGain(a), 0);
  const realized = getTotalRealizedGain(transactions);
  const costBasis = filtered.reduce((s, a) => s + getAssetCostBasis(a), 0);

  const gainData = [...filtered]
    .map(a => ({ code: a.code, pct: getAssetUnrealizedGainPercent(a), abs: getAssetUnrealizedGain(a) }))
    .sort((a, b) => b.pct - a.pct);

  // Asset class breakdown
  const classMap: Record<string, { value: number; cost: number }> = {};
  filtered.forEach(a => {
    if (!classMap[a.assetClass]) classMap[a.assetClass] = { value: 0, cost: 0 };
    classMap[a.assetClass].value += getAssetCurrentValue(a);
    classMap[a.assetClass].cost += getAssetCostBasis(a);
  });
  const classPerf = Object.entries(classMap).map(([name, d]) => ({
    name, value: d.value, pct: d.cost > 0 ? ((d.value - d.cost) / d.cost) * 100 : 0,
    color: resolveClassColor(name, assetClasses),
  })).sort((a, b) => b.pct - a.pct);

  // Alerts: funds with targetValue set and more than 10% off target
  const alerts = buckets.filter(b => {
    if (b.id === 'ruong-chua' || b.targetValue <= 0) return false;
    const { pct } = getBucketProgress(b, assets);
    return pct !== null && Math.abs(pct - 100) > 10;
  });

  const sortedTx = [...transactions].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* Cơ cấu tài sản + Rebalancing */}
      <AssetStructureModule />

      {/* Filters */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Filter size={13} color="var(--tx-4)" />
        {[
          {
            value: filterClass, onChange: setFilterClass,
            options: [
              { value: '', label: 'Tất cả lớp tài sản' },
              ...(assetClasses || []).map(c => ({ value: c.name, label: c.name })),
            ],
          },
          {
            value: filterBucket, onChange: setFilterBucket,
            options: [{ value: '', label: 'Tất cả quỹ' }, ...buckets.map(b => ({ value: b.id, label: b.name }))],
          },
        ].map((sel, i) => (
          <select
            key={i}
            value={sel.value}
            onChange={(e) => sel.onChange(e.target.value)}
            style={{
              height: 34, padding: '0 10px', borderRadius: 8, fontSize: 12, outline: 'none', cursor: 'pointer',
              background: 'var(--bg-input)', border: `1px solid ${sel.value ? 'rgba(16,185,129,0.3)' : 'var(--bd)'}`,
              color: sel.value ? '#10b981' : 'var(--tx-3)',
            }}
          >
            {sel.options.map(o => <option key={o.value} value={o.value} style={{ background: 'var(--bg-card)' }}>{o.label}</option>)}
          </select>
        ))}
        {(filterClass || filterBucket) && (
          <button
            onClick={() => { setFilterClass(''); setFilterBucket(''); }}
            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 7, fontSize: 11, cursor: 'pointer', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.15)', color: '#f87171' }}
          >
            <X size={11} /> Xóa bộ lọc
          </button>
        )}
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          {alerts.map(b => {
            const { nav, diff } = getBucketProgress(b, assets);
            const over = diff !== null && diff >= 0;
            return (
              <div key={b.id} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '11px 16px', borderRadius: 10,
                background: 'rgba(251,191,36,0.05)', border: '1px solid rgba(251,191,36,0.2)',
              }}>
                <AlertTriangle size={14} color="#fbbf24" style={{ flexShrink: 0 }} />
                <span style={{ fontSize: 13, color: 'var(--tx-1)' }}>
                  <span style={{ color: b.color, fontWeight: 600 }}>{b.name}</span>
                  {over ? ' đang dư ' : ' còn thiếu '}
                  <span style={{ color: '#fbbf24', fontWeight: 700 }}><Money value={Math.abs(diff!)} /></span>
                  {' '}so với mục tiêu <Money value={b.targetValue} /> — Thực tế: <Money value={nav} />
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* KPI */}
      <div className="grid-cols-3-sm">
        {([
          { label: 'Giá trị danh mục', value: <Money value={totalNAV} />, sub: <><span>Giá vốn: </span><Money value={costBasis} /></>, color: 'var(--tx-1)' },
          {
            label: 'Lợi nhuận chưa thực hiện', value: <Money value={unrealized} />,
            sub: `${unrealized >= 0 ? '+' : ''}${costBasis > 0 ? ((unrealized / costBasis) * 100).toFixed(2) : 0}% ROI`,
            color: unrealized >= 0 ? '#10b981' : '#f87171',
          },
          { label: 'Lợi nhuận đã thực hiện', value: <Money value={realized} />, sub: 'Tổng lịch sử chốt lời', color: '#38bdf8' },
        ] as { label: string; value: React.ReactNode; sub: React.ReactNode; color: string }[]).map((s) => (
          <div key={s.label} style={{ padding: '18px 20px', borderRadius: 14, background: 'var(--bg-card)', border: '1px solid var(--bd)' }}>
            <div style={{ fontSize: 11, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500, marginBottom: 8 }}>{s.label}</div>
            <div className="rsp-value" style={{ color: s.color, marginBottom: 4 }}>{s.value}</div>
            <div style={{ fontSize: 12, color: 'var(--tx-4)' }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Charts row */}
      <div className="rsp-grid-2" style={{ gap: 12 }}>

        {/* Gain per asset */}
        <Card>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--bd)' }}>
            <div style={{ fontSize: 12, color: 'var(--tx-4)' }}>Lợi nhuận chưa TH theo tài sản (%)</div>
          </div>
          <CardBody style={{ padding: '16px 8px' }}>
            <ResponsiveContainer width="100%" height={Math.min(gainData.length * 42 + 20, 480)}>
              <BarChart data={gainData} layout="vertical" margin={{ left: 0, right: 24 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--bd)" horizontal={false} />
                <XAxis
                  type="number" tickFormatter={(v) => `${v.toFixed(0)}%`}
                  tick={{ fill: 'var(--tx-4)', fontSize: 10 }} axisLine={false} tickLine={false}
                />
                <YAxis dataKey="code" type="category" tick={{ fill: 'var(--chart-tick)', fontSize: 12, fontWeight: 500 }} axisLine={false} tickLine={false} width={48} />
                <Tooltip formatter={(v: any) => [`${Number(v).toFixed(2)}%`, 'Lợi nhuận']} {...tooltip} />
                <Bar dataKey="pct" radius={[0, 4, 4, 0]} maxBarSize={22}>
                  {gainData.map((d) => <Cell key={d.code} fill={d.pct >= 0 ? '#10b981' : '#f87171'} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardBody>
        </Card>

        {/* Class performance */}
        <Card>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--bd)' }}>
            <div style={{ fontSize: 12, color: 'var(--tx-4)' }}>Hiệu suất theo lớp tài sản</div>
          </div>
          <CardBody>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {classPerf.map((c) => {
                const maxPct = Math.max(...classPerf.map(x => Math.abs(x.pct)), 1);
                const barW = Math.min((Math.abs(c.pct) / maxPct) * 100, 100);
                return (
                  <div key={c.name}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <div style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: c.color }} />
                        <span style={{ fontSize: 12, color: 'var(--tx-3)' }}>{c.name}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 12, color: 'var(--tx-3)' }}><Money value={c.value} /></span>
                        <span style={{
                          fontSize: 12, fontWeight: 700, padding: '2px 8px', borderRadius: 5,
                          background: c.pct >= 0 ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
                          color: c.pct >= 0 ? '#10b981' : '#f87171',
                        }}>
                          {c.pct >= 0 ? '+' : ''}{c.pct.toFixed(2)}%
                        </span>
                      </div>
                    </div>
                    <div style={{ height: 4, borderRadius: 2, backgroundColor: 'var(--bd)', overflow: 'hidden' }}>
                      <div style={{ height: '100%', borderRadius: 2, width: `${barW}%`, backgroundColor: c.pct >= 0 ? c.color : '#f87171', transition: 'width 0.5s ease' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Transaction Ledger */}
      <Card>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--bd)' }}>
          <div style={{ fontSize: 12, color: 'var(--tx-4)' }}>Sổ cái giao dịch</div>
        </div>
        <div className="table-wrap">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--bd)' }}>
              {['Ngày', 'Loại', 'Tài sản', 'Số lượng', 'Đơn giá', 'Tổng giá trị', 'Lãi/lỗ thực', 'Ghi chú'].map((h, i) => (
                <th key={h} style={{
                  padding: '9px 16px',
                  textAlign: i > 2 ? 'right' as const : 'left' as const,
                  fontSize: 11, fontWeight: 500, color: 'var(--tx-4)',
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                  whiteSpace: 'nowrap',
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedTx.length === 0 && (
              <tr><td colSpan={8} style={{ padding: '40px 20px', textAlign: 'center', fontSize: 12, color: 'var(--tx-5)' }}>Chưa có giao dịch nào</td></tr>
            )}
            {sortedTx.map((tx, i) => {
              const cfg = TX_LABELS[tx.type] || { label: tx.type, color: 'var(--tx-3)' };
              return (
                <tr
                  key={tx.id}
                  style={{ borderBottom: i < sortedTx.length - 1 ? '1px solid var(--bd-sub)' : 'none', transition: 'background 0.1s' }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLTableRowElement).style.background = 'var(--bg-row-hover)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLTableRowElement).style.background = 'transparent'; }}
                >
                  <td style={{ padding: '11px 16px', fontSize: 12, color: 'var(--tx-3)', whiteSpace: 'nowrap' }}>{tx.date}</td>
                  <td style={{ padding: '11px 16px' }}>
                    <span style={{ fontSize: 11, padding: '3px 9px', borderRadius: 5, fontWeight: 500, background: `${cfg.color}18`, color: cfg.color }}>{cfg.label}</span>
                  </td>
                  <td style={{ padding: '11px 16px' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx-1)' }}>{tx.assetCode}</div>
                    <div style={{ fontSize: 11, color: 'var(--tx-4)', marginTop: 1, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tx.assetName}</div>
                  </td>
                  <td style={{ padding: '11px 16px', textAlign: 'right', fontSize: 12, color: 'var(--tx-4)' }}>
                    {tx.quantity > 0 ? tx.quantity.toLocaleString('vi-VN') : '—'}
                  </td>
                  <td style={{ padding: '11px 16px', textAlign: 'right', fontSize: 12, color: 'var(--tx-3)' }}>
                    {tx.price > 0 ? <Money value={tx.price} /> : '—'}
                  </td>
                  <td style={{ padding: '11px 16px', textAlign: 'right', fontSize: 13, fontWeight: 600, color: 'var(--tx-1)' }}>
                    <Money value={tx.totalValue} />
                  </td>
                  <td style={{ padding: '11px 16px', textAlign: 'right' }}>
                    {tx.realizedGain !== undefined ? (
                      <span style={{ fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 3, color: tx.realizedGain >= 0 ? '#10b981' : '#f87171' }}>
                        {tx.realizedGain >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                        <Money value={tx.realizedGain} prefix={tx.realizedGain >= 0 ? '+' : ''} />
                      </span>
                    ) : <span style={{ color: 'var(--tx-5)' }}>—</span>}
                  </td>
                  <td style={{ padding: '11px 16px', textAlign: 'right', fontSize: 11, color: 'var(--tx-4)', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {tx.notes || '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </Card>
    </div>
  );
}
